import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Wallet, ArrowRight, Receipt, RefreshCw } from "lucide-react";

const rupiah = (n) => "Rp" + (n || 0).toLocaleString("id-ID");
const tanggal = (s) => {
  if (!s) return "-";
  try {
    return new Date(s).toLocaleString("id-ID", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return s; }
};
const tanggalOnly = (s) => {
  if (!s) return "-";
  try {
    return new Date(s).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return s; }
};

const STATUS = {
  paid: { label: "Berhasil", cls: "bg-[#4A6B53]/15 text-[#4A6B53]" },
  pending: { label: "Menunggu", cls: "bg-[#F4B942]/20 text-[#8a6b12]" },
  failed: { label: "Gagal", cls: "bg-[#E25E3E]/15 text-[#C94F31]" },
};

export default function RiwayatPembelian() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(null);

  const load = () =>
    api.get("/payments/history")
      .then((r) => setData(r.data))
      .catch(() => setData({ credits: 0, orders: [] }))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const checkStatus = async (orderId) => {
    setChecking(orderId);
    try {
      const { data: res } = await api.post(`/payments/${orderId}/check`);
      if (res.status === "paid") toast.success("Pembayaran berhasil! Kredit sudah masuk.");
      else if (res.status === "failed") toast.error("Pembayaran gagal atau kedaluwarsa.");
      else toast.info(res.note || "Pembayaran masih menunggu. Coba lagi nanti.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setChecking(null);
    }
  };

  const orders = data?.orders || [];

  return (
    <AppShell>
      <div className="mb-8">
        <p className="text-[#5C5F5C]">Pembayaran & Akses</p>
        <h1 className="font-heading font-black text-3xl sm:text-4xl text-[#1A1C1A]">Riwayat Pembelian</h1>
      </div>

      {data?.credits > 0 && (
      <div className="clay-card p-6 mb-6 flex flex-wrap items-center justify-between gap-4 bg-[#1A1C1A] text-white border-[#1A1C1A]" data-testid="credit-balance-card">
        <div className="flex items-center gap-3">
          <span className="h-12 w-12 rounded-xl bg-[#F4B942] text-[#1A1C1A] grid place-items-center"><Wallet className="h-6 w-6" /></span>
          <div>
            <p className="text-white/70 text-sm">Sisa Saldo Kredit</p>
            <p className="font-heading font-black text-3xl" data-testid="riwayat-credit-balance">{data?.credits ?? 0} <span className="text-lg font-medium text-white/70">kredit</span></p>
            {data?.next_expiry && (
              <p className="text-xs text-[#F4B942] mt-1" data-testid="next-expiry">Kredit terdekat kedaluwarsa: {tanggalOnly(data.next_expiry)}</p>
            )}
          </div>
        </div>
        <Link to="/tryout" data-testid="beli-kredit-link" className="px-5 py-2.5 rounded-full bg-[#E25E3E] text-white text-sm font-medium hover:bg-[#C94F31] transition-colors flex items-center gap-2">
          Ke TryOut <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      )}

      <div className="clay-card p-6" data-testid="riwayat-table">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#5C5F5C] mb-4"><Receipt className="h-4 w-4" /> Transaksi</div>

        {loading ? (
          <p className="text-sm text-[#5C5F5C] py-6 text-center">Memuat...</p>
        ) : orders.length === 0 ? (
          <div className="py-10 text-center" data-testid="riwayat-empty">
            <p className="text-[#5C5F5C]">Belum ada pembelian.</p>
            <Link to="/tryout" className="mt-3 inline-flex items-center gap-1 text-[#E25E3E] font-medium text-sm">Lihat paket TryOut <ArrowRight className="h-4 w-4" /></Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead>
                <tr className="text-left text-[#5C5F5C] border-b border-[#E5E0D8]">
                  <th className="py-3 pr-4 font-semibold">Tanggal</th>
                  <th className="py-3 pr-4 font-semibold">Paket</th>
                  <th className="py-3 pr-4 font-semibold">Jumlah</th>
                  <th className="py-3 pr-4 font-semibold">Isi</th>
                  <th className="py-3 pr-4 font-semibold">Masa Berlaku</th>
                  <th className="py-3 pr-4 font-semibold">Status</th>
                  <th className="py-3 font-semibold">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => {
                  const s = STATUS[o.status] || STATUS.pending;
                  return (
                    <tr key={o.order_id} className="border-b border-[#F0ECE4] last:border-0" data-testid={`riwayat-row-${o.order_id}`}>
                      <td className="py-3 pr-4 text-[#1A1C1A] whitespace-nowrap">{tanggal(o.paid_at || o.created_at)}</td>
                      <td className="py-3 pr-4 text-[#1A1C1A]">{o.package_name}</td>
                      <td className="py-3 pr-4 text-[#1A1C1A] whitespace-nowrap">{rupiah(o.amount)}</td>
                      <td className="py-3 pr-4 text-[#1A1C1A] whitespace-nowrap">{o.isi}</td>
                      <td className="py-3 pr-4 whitespace-nowrap" data-testid={`riwayat-berlaku-${o.order_id}`}>
                        {o.status !== "paid" ? (
                          <span className="text-[#5C5F5C]">-</span>
                        ) : o.expires_at ? (
                          o.expired ? (
                            <span className="text-[#C94F31] font-medium">Kedaluwarsa</span>
                          ) : (
                            <span className="text-[#1A1C1A]">s/d {tanggalOnly(o.expires_at)}{typeof o.remaining === "number" ? ` · sisa ${o.remaining}` : ""}</span>
                          )
                        ) : (
                          <span className="text-[#5C5F5C]">Selamanya</span>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${s.cls}`} data-testid={`riwayat-status-${o.order_id}`}>{s.label}</span>
                      </td>
                      <td className="py-3">
                        {o.status === "pending" && (
                          <button
                            onClick={() => checkStatus(o.order_id)}
                            disabled={checking === o.order_id}
                            data-testid={`cek-status-${o.order_id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#1A1C1A]/20 text-xs font-medium text-[#1A1C1A] hover:bg-[#1A1C1A]/5 transition-colors disabled:opacity-60"
                          >
                            <RefreshCw className={`h-3.5 w-3.5 ${checking === o.order_id ? "animate-spin" : ""}`} />
                            {checking === o.order_id ? "Mengecek..." : "Cek Status"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}
